import { BreakpointObserver } from '@angular/cdk/layout';
import { ComponentType } from '@angular/cdk/portal';
import { Injectable, inject } from '@angular/core';
import { MatDialog, MatDialogConfig, MatDialogRef } from '@angular/material/dialog';
import { takeUntil } from 'rxjs/operators';

/** The handset breakpoint used by the existing project selector dialog. */
export const HANDSET_DIALOG_QUERY = '(max-width: 768px)';

/** Applied to the overlay pane while a dialog is using the handset layout. */
export const HANDSET_FULLSCREEN_DIALOG_PANEL_CLASS = 'handset-fullscreen-dialog';

/**
 * Opens dialogs at their supplied desktop dimensions and makes them fill the viewport on handsets.
 *
 * Keeping the breakpoint subscription here gives every caller the same responsive shell without
 * requiring each dialog component to manage its own `BreakpointObserver` lifecycle.
 */
@Injectable({ providedIn: 'root' })
export class HandsetDialogService {
  private dialog = inject(MatDialog);
  private breakpointObserver = inject(BreakpointObserver);

  // Match MatDialog.open's defaults so migrating an existing call site does not narrow its
  // afterClosed() result to unknown. Callers can still provide D and R explicitly when desired.
  // `any` intentionally preserves MatDialog's backward-compatible result contract.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  open<T, D = unknown, R = any>(component: ComponentType<T>, config?: MatDialogConfig<D>): MatDialogRef<T, R> {
    const desktopWidth = config?.width ?? '';
    const desktopHeight = config?.height ?? '';
    const startsOnHandset = this.breakpointObserver.isMatched(HANDSET_DIALOG_QUERY);
    const initialConfig = startsOnHandset
      ? {
          ...config,
          width: '100vw',
          height: '100dvh',
          maxWidth: '100vw',
          maxHeight: '100dvh',
          panelClass: this.withPanelClass(config?.panelClass, HANDSET_FULLSCREEN_DIALOG_PANEL_CLASS),
        }
      : config;
    const dialogRef = this.dialog.open<T, D, R>(component, initialConfig);

    this.breakpointObserver
      .observe(HANDSET_DIALOG_QUERY)
      .pipe(takeUntil(dialogRef.afterClosed()))
      .subscribe(({ matches }) => {
        if (matches) {
          dialogRef.updateSize('100vw', '100dvh');
          dialogRef.addPanelClass(HANDSET_FULLSCREEN_DIALOG_PANEL_CLASS);
          return;
        }

        dialogRef.updateSize(desktopWidth, desktopHeight);
        dialogRef.removePanelClass(HANDSET_FULLSCREEN_DIALOG_PANEL_CLASS);
      });

    return dialogRef;
  }

  private withPanelClass(panelClass: string | string[] | undefined, requiredClass: string): string[] {
    const classes = Array.isArray(panelClass) ? panelClass : panelClass ? [panelClass] : [];
    return classes.includes(requiredClass) ? classes : [...classes, requiredClass];
  }
}
